import { useEffect, useState } from 'react';

import { tableStore, type TableRow } from '../../stores/table-store';
import { useProStudioCtxSelector } from '../../xstate/app.xstate';

/** Published versions don't change, so their rows are fetched once per session. */
const cache = new Map<string, Promise<TableRow[]>>();

export type VersionRowsState =
  | { status: 'idle' | 'loading'; rows: null; error: null }
  | { status: 'ready'; rows: TableRow[]; error: null }
  | { status: 'error'; rows: null; error: string };

/** A version's solution version id: `<customer>-<bot>-<version>`. */
export const useSolutionVersionId = () => {
  const { customerId, botName } = useProStudioCtxSelector((ctx) => ({
    customerId: ctx.customerId,
    botName: ctx.botName,
  }));
  return (version: string | null | undefined) =>
    customerId && botName && version
      ? `${customerId}-${botName}-${version}`
      : null;
};

/** The nodes of one published version, or null while none is asked for. */
export const useVersionRows = (
  solutionVersionId: string | null
): VersionRowsState => {
  const [state, setState] = useState<VersionRowsState>({
    status: 'idle',
    rows: null,
    error: null,
  });

  useEffect(() => {
    if (!solutionVersionId) {
      setState({ status: 'idle', rows: null, error: null });
      return;
    }
    let cancelled = false;
    setState({ status: 'loading', rows: null, error: null });

    let request = cache.get(solutionVersionId);
    if (!request) {
      request = tableStore.fetchVersionRows(solutionVersionId);
      cache.set(solutionVersionId, request);
      request.catch(() => cache.delete(solutionVersionId));
    }

    request.then(
      (rows) => {
        if (!cancelled) setState({ status: 'ready', rows, error: null });
      },
      (e: unknown) => {
        if (!cancelled) {
          setState({
            status: 'error',
            rows: null,
            error: e instanceof Error ? e.message : String(e),
          });
        }
      }
    );

    return () => {
      cancelled = true;
    };
  }, [solutionVersionId]);

  return state;
};
